import React from 'react';
import {Expressions} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {EText} from '@packages/systems/wfdl-ast';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {styledDiv, emotionStyled, tokens} from '@packages/utilities/styled';
import {InfoIcon} from '@packages/systems/spring';
import * as KeyUtils from '@packages/utilities/key-utils';
import TextInput from '@packages/systems/design-system/TextInput';
import {reportBug} from '@packages/utilities/reporter';
import {
  rightSidebarLabelWidth,
  rightSidebarWidth,
} from '@packages/systems/design-system/constants/DesignerUI';
import {Label} from '@packages/systems/ExpressionEditor-utils-ui';

import fetch from 'isomorphic-fetch';

import {
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const selectGoogleMapsApiKey = (state: any) =>
  state.SiteDataStore.googleMapsApiKey;

const Loader = styledDiv(({theme: {gutters, colors}}) => ({
  display: 'inline-block',
  position: 'absolute',
  right: gutters.x8,
  top: '-6px',
  zIndex: 1,
  fontSize: '6px',
  width: '6px',
  height: '6px',
  margin: '14px 0 0 21px',
  borderRadius: '100px',
  background: colors.backgroundDeepest,
  animation: 'wf-blink 2s infinite',
  boxShadow: '-1.5em 0em #d9d9d9, 1.5em 0em ' + colors.backgroundDeepest,
}));

const GetAddressLabel = styledDiv(({theme: {gutters, colors, typography}}) => ({
  ...typography.label,
  color: colors.foregroundDim,
  display: 'flex',
  gap: gutters.base,
  alignItems: 'center',
  marginBottom: gutters.base * 2,
  lineHeight: 1,
  marginTop: gutters.base * 2,
}));

const ErrorWrapper = emotionStyled('span')({
  color: tokens.colors.redText,
});

const TwoColumns = styledDiv(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: `${rightSidebarLabelWidth}px ${
    rightSidebarWidth - rightSidebarLabelWidth - theme.gutters.normal * 3
  }px`,
  gridGap: `${theme.gutters.normal}px`,
  alignItems: 'center',
}));

const UNABLE_TO_CONNECT = 'Currently unable to connect to Google maps';

export const GetAddressInput = ({address, dispatch, contextualExpr}: any) => {
  const [value, setValue] = React.useState<string>(
    ContextualExpressions.getText(stripAnn(address)) || ''
  );
  const [errorMsg, setErrorMsg] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [focus, setFocus] = React.useState<boolean>(false);
  const [addressText, setAddressText] = React.useState<string>(
    ContextualExpressions.getText(stripAnn(address)) || ''
  );
  const hasValidKey = useDesignerSelector(selectGoogleMapsApiKey);
  const expr = stripAll(contextualExpr);

  React.useEffect(() => {
    const nextAddressText =
      ContextualExpressions.getText(stripAnn(address)) || '';
    if (nextAddressText !== addressText) {
      setAddressText(nextAddressText);
      setValue(nextAddressText);
    }
  }, [address, addressText]);

  React.useEffect(() => setErrorMsg(''), [value]);

  const commitAddress = async () => {
    setFocus(false);
    setErrorMsg('');

    try {
      if (
        value &&
        value.length > 0 &&
        hasValidKey &&
        ContextualExpressions.getText(stripAnn(address)) !== value
      ) {
        setLoading(true);
        const response = await fetch(`/api/geocode?address=${value}`);

        const data = await response.json();

        // google maps API returns response: {results: [], status: 'OK'} when no address found
        if (data.results.length === 0) {
          setErrorMsg(`Unable to find address: ${value}`);
        } else {
          setValue(data.results[0].formatted_address);

          const latLng =
            data.results[0].geometry.location.lat +
            ',' +
            data.results[0].geometry.location.lng;

          const newExpr = Expressions.setIn(
            Expressions.setIn(
              expr,
              [
                {in: 'Record', at: 'widget'},
                {in: 'Record', at: 'address'},
              ],
              EText(data.results[0].formatted_address)
            ),
            [
              {in: 'Record', at: 'attr'},
              {in: 'Record', at: 'data-widget-latlng'},
            ],
            EText(latLng)
          );
          dispatch(updateExpressionAction(newExpr));
        }
      }
    } catch {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('GeocodeAPIError', 'Request error', {
        error: UNABLE_TO_CONNECT,
      });
      setErrorMsg(UNABLE_TO_CONNECT);
    }

    setLoading(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {which} = e;
    if (which === KeyUtils.ENTER) {
      commitAddress();
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const onBlur = () => {
    commitAddress();
  };

  return (
    <TwoColumns>
      <Label automationId="mapWidget-address-label">Address</Label>
      <div style={{position: 'relative'}}>
        {loading && <Loader />}
        <Hitarea>
          <TextInput
            type="text"
            placeholder="e.g. London"
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            focus={focus}
            disabled={!hasValidKey}
          />
        </Hitarea>
        {errorMsg ? (
          <GetAddressLabel>
            <ErrorWrapper>{errorMsg}</ErrorWrapper>
          </GetAddressLabel>
        ) : null}
        <GetAddressLabel>
          <InfoIcon size={10} />
          <span>Actual map on published site</span>
        </GetAddressLabel>
      </div>
    </TwoColumns>
  );
};
